import axios, { AxiosError } from "axios";
import Cookies from "js-cookie";
import { getApiBaseUrl } from "../utils/apiConfig";
import { mapAPIError } from "./mapAPIError";
import { sessionManager } from "../utils/sessionManager";
import type { Middleware } from "@reduxjs/toolkit";

// Ceiling on how long a single API request may stall before we give up. Without
// this, a request whose socket half-opens (e.g. a clinician walking out of Wi-Fi
// range mid-request) never settles, so the matching Success/Fail action is never
// dispatched and any loading flag driving a spinner stays true forever.
const REQUEST_TIMEOUT_MS = 60_000;

interface ApiAction {
  type: string;
  payload?: {
    apiName?: string;
    data?: any;
    formData?: boolean;
    method?: string;
    onFail?: (message: string) => any;
    onSuccess?: (data: any, requestData: any) => any;
    url?: string;
    signal?: AbortSignal;
    skipLogout?: boolean;
    timeout?: number;
    // Allow any additional dynamic properties
    [key: string]: any;
  };
  // Allow any additional properties at the action level too
  [key: string]: any;
}

export const api: Middleware =
  ({ dispatch }) =>
  (next) =>
  async (action: ApiAction) => {
    if (!action?.payload?.apiName) return next(action);
    next(action);

    const successStatuses = [204, 200, 201];
    const { apiName, data, formData = false, method, onFail, onSuccess, url, signal, timeout } = action.payload;
    // Get API base URL based on environment
    const baseURL = getApiBaseUrl();
    const abortController = new AbortController();

    dispatch({ payload: apiName, type: "globalLoading/start" });

    dispatch({ type: `${apiName}/${action.type}` });
    const headers = {
      "X-CSRFToken": Cookies.get("csrftoken"),
    };

    if (method !== "get" && method !== "GET") {
      axios.defaults.xsrfHeaderName = "x-csrftoken";
      axios.defaults.xsrfCookieName = "csrftoken";
      axios.defaults.withCredentials = true;
    }

    if (formData) {
      headers["Content-Type"] = "multipart/form-data";
    }
    try {
      //call the getcsrf api if csrf token not found
      if (!Cookies.get("csrftoken") && method !== "get" && method !== "GET") {
        await axios.request({
          baseURL,
          method: "get",
          // The preflight must be capped too: it is awaited before the main
          // request, so a stalled CSRF fetch would hang the middleware here and
          // never let the main request (or its Fail action) run.
          timeout: REQUEST_TIMEOUT_MS,
          url: "/api/csrf-token/",
        });
      }

      const response = await axios.request({
        baseURL,
        data,
        headers,
        method,
        // Only a positive override wins; 0/negative would mean "no timeout" to
        // axios and silently reintroduce the hang, so fall back to the default.
        timeout: timeout && timeout > 0 ? timeout : REQUEST_TIMEOUT_MS,
        url,
        signal: signal ? signal : abortController.signal,
      });

      if (successStatuses.includes(response.status)) {
        dispatch({
          payload: response.data,
          type: `${apiName}/${action.type}Success`,
        });
      }

      dispatch({ payload: apiName, type: "globalLoading/stop" });

      onSuccess && dispatch(onSuccess(response.data, data));
    } catch (err) {
      const error = err as AxiosError;
      const { message, response } = error;
      const payload = {
        message: apiName === "Encounter" && action.type === "saveEncounterNote" ? "" : mapAPIError(error, response?.status),
        statusCode: response?.status,
        statusText: response?.data,
      };
      dispatch({
        type: `${apiName}/${action.type}Fail`,
        payload,
      });

      // Flag on API request payload to skip the normal logout behavior on 401/403,
      // reserved for specific calls during the login/logout processes.
      const skipLogout = action.payload?.skipLogout ?? false;

      if ((payload.statusCode === 403 || payload.statusCode === 401) && !skipLogout) {
        // Abort all ongoing requests if a 403/401 status is encountered
        abortController.abort();

        // Use centralized session manager for consistent handling
        sessionManager.handleSessionExpiry("Redux API");
      } else {
        dispatch({ payload, type: `globalErrors/get` });
      }
      dispatch({ payload: apiName, type: "globalLoading/stop" });
      onFail && dispatch(onFail(message));
    }
  };
